// Экран ошибки: что он говорит и на каком языке.
//
// ПОЧЕМУ ОТДЕЛЬНЫМ ФАЙЛОМ. Один и тот же текст печатают ДВЕ разные границы —
// клиентская (routes/__root.tsx, граница роутера) и серверная (lib/error-page.ts,
// ответ 500 из воркера). Они жили порознь и говорили одно и то же слово в слово
// по случайности; разойтись им было негде только потому, что никто их не правил.
//
// ЧТО МЫ ЗДЕСЬ ЧИНИМ. Обе границы утверждали «Something went wrong on our end»
// на ЛЮБУЮ ошибку. Замер 7 сентября: класс видел «This page didn't load» на
// каждом вопросе, а Cloudflare Observability за то же окно — 1886 успехов и
// НОЛЬ ошибок. То есть до воркера запрос не доходил, ломалось соединение, а
// экран уверенно называл виноватым нас. Он соврал и детям, и нам.
//
// Экран не утверждает, где сломалось, если не знает.

export type ErrorLang = "ru" | "az" | "en";

/**
 * Обрыв связи или отказ сервера.
 *
 * РАЗЛИЧАЕМ ПО ИМЕНИ ОШИБКИ, А НЕ ПО ДОГАДКЕ. Браузеры называют потерю сети
 * тремя разными фразами (Chrome «Failed to fetch», Firefox «NetworkError…»,
 * Safari «Load failed»), и ровно эти три уже перечислены в ignoreErrors
 * Sentry — там же, где стоит комментарий «уже показано пользователю в UI, в
 * Sentry ценности нет». Список один, и это не совпадение: он описывает один и
 * тот же класс события с двух сторон.
 *
 * `navigator.onLine === false` — второй признак, и он сильнее текста: браузер
 * знает про свой сетевой стек больше, чем мы про его формулировки. Но он же и
 * лживее в другую сторону: `true` не означает, что сеть работает, — поэтому
 * он ДОБАВЛЯЕТ уверенности, а не отнимает.
 */
export function looksLikeNetworkDrop(error: unknown): boolean {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error ?? "");
  return /Failed to fetch|NetworkError|Load failed|AbortError|ERR_NETWORK|ERR_INTERNET/i.test(
    message,
  );
}

/**
 * Язык экрана ошибки.
 *
 * Из `<html lang>`, если страница успела его проставить, иначе из языка
 * браузера. Ребёнок, у которого сломалась загрузка, не выбирал язык — и
 * спрашивать его в этот момент поздно.
 */
export function errorLang(): ErrorLang {
  const raw =
    (typeof document !== "undefined" ? document.documentElement.lang : "") ||
    (typeof navigator !== "undefined" ? navigator.language : "") ||
    "";
  const code = raw.slice(0, 2).toLowerCase();
  if (code === "az") return "az";
  if (code === "en") return "en";
  return "ru";
}

type Texts = {
  /**
   * Заголовки РАЗНЫЕ у двух случаев, и это не стиль, а условие измерения.
   *
   * Замысел был такой: тексты различаются, школа называет фразу, мы отделяем
   * обрыв связи от настоящей пятисотки без всякой телеметрии. Но заголовок
   * стоял ОДИН на оба случая — а он и есть та крупная строка, которую ребёнок
   * запомнит и перескажет учителю. Прибор, у которого обе шкалы показывают
   * одно, ничего не измеряет.
   *
   * Первое слово тоже разведено намеренно. По-азербайджански оба текста
   * начинались на «B» («Bağlantı» / «Bizim»), и в пересказе «начиналось на
   * B…» не различает ничего. Теперь «Bağlantı» против «Xəta».
   */
  titleNetwork: string;
  titleServer: string;
  /** Обрыв связи: мы знаем, что до нас не дошло. */
  network: string;
  /**
   * Отказ сервера: здесь «у нас» — правда.
   *
   * СОВЕТА УЙТИ С ЭКРАНА ЗДЕСЬ НЕТ. Стояло «попробуй ещё раз или вернись на
   * главную» — посреди экзамена это предложение выйти из теста, и ребёнок,
   * который слушается написанного, потеряет попытку по нашему совету.
   *
   * Обещания «ответы сохранены» тут тоже нет и быть не может: на серверной
   * границе «Повторить» — это location.reload(), и снимок в памяти теряется.
   * Вместо него сказано то, что правда и что ребёнку в этот момент нужнее:
   * дело не в нём.
   */
  server: string;
  retry: string;
  home: string;
};

/**
 * ОБЕЩАНИЕ «ОТВЕТЫ СОХРАНЕНЫ» ПРОВЕРЕНО, А НЕ НАПИСАНО ДЛЯ УСПОКОЕНИЯ.
 *
 * Сохранение шлёт ВЕСЬ снимок ответов на каждом клике (queueSave в
 * session.$token.test.tsx), а «Повторить» на клиентской границе делает
 * router.invalidate() + reset(), то есть остаётся на странице и не роняет
 * состояние React. Значит сохранённое сохранено, а несохранённое уедет
 * следующим же успешным сохранением.
 *
 * На серверной границе (500 из воркера) «Повторить» — это location.reload(),
 * и там снимок в памяти теряется. Поэтому обещание стоит ТОЛЬКО в сетевой
 * ветке клиентского экрана, а не во всех четырёх текстах.
 */
export const ERROR_TEXTS: Record<ErrorLang, Texts> = {
  ru: {
    titleNetwork: "Связь прервалась",
    titleServer: "Ошибка у нас",
    network: "Нажми «Повторить» — ответы сохранены.",
    server: "Попробуй ещё раз — дело не в тебе.",
    retry: "Повторить",
    home: "На главную",
  },
  az: {
    // Ветка обрыва — ТЕКСТ ДЖАВИДА, принят дословно. На экране заголовок и
    // абзац читаются подряд и дают ровно ту фразу, которую он утвердил:
    // «Bağlantı kəsildi. «Yenidən» düyməsini bas — cavablar saxlanılıb.»
    // Разделены они не ради стиля: одинаковый заголовок на обеих ветках делал
    // невозможным измерение, ради которого тексты и разводились.
    titleNetwork: "Bağlantı kəsildi",
    // Серверная ветка — тоже подписана носителем, 7 сентября. Азербайджанский
    // в продукте подписывает носитель: то же правило, что у отчётов 16-18.
    titleServer: "Xəta bizim tərəfimizdədir",
    network: "«Yenidən» düyməsini bas — cavablar saxlanılıb.",
    server: "Yenidən cəhd et — məsələ səndə deyil.",
    retry: "Yenidən",
    home: "Ana səhifə",
  },
  en: {
    titleNetwork: "The connection dropped",
    titleServer: "Something broke on our end",
    network: "Press “Try again” — your answers are saved.",
    server: "Try again — it's not something you did.",
    retry: "Try again",
    home: "Go home",
  },
};

/**
 * Язык серверного экрана — из ЗАПРОСА, потому что браузера там нет.
 *
 * Порядок: путь сильнее заголовка. Ребёнок, пришедший на /az/…, назвал язык
 * действием; Accept-Language — только предпочтение его устройства, и оно
 * бывает чужим (общий компьютер в классе).
 *
 * Дефект тот же, что чинили на клиенте, и половина та же: текст пятисотки
 * честен — там «у нас» правда, — а язык не тот. День экзамена худший момент,
 * чтобы азербайджанский ребёнок читал английский.
 */
export function errorLangFromRequest(request: Request | null | undefined): ErrorLang {
  if (!request) return "ru";
  try {
    const path = new URL(request.url).pathname;
    if (path === "/az" || path.startsWith("/az/") || path.endsWith("_az")) return "az";
    if (path === "/en" || path.startsWith("/en/") || path.endsWith("_en")) return "en";
  } catch {
    // Непарсящийся URL — не повод падать на экране ошибки.
  }
  const accept = (request.headers.get("accept-language") ?? "").toLowerCase();
  const first = accept.split(",")[0]?.trim().slice(0, 2) ?? "";
  if (first === "az") return "az";
  if (first === "en") return "en";
  return "ru";
}
